/**
 * Utility functions for feedback status management.
 * Used by admin feedback detail page for status transitions.
 *
 * Issue #2636: Updated to handle all FeedbackStatus values including
 * ISSUE_CREATED, IMPLEMENTING, and CLOSED statuses.
 */
import type { FeedbackStatus } from '@prisma/client-cadence'

/**
 * Returns the available next status actions based on current status.
 * Defines the valid status transition workflow.
 *
 * Updated for Issue #4296: Added ACKNOWLEDGED, PLANNED, DEFERRED statuses
 * for triage mode workflow.
 */
export function getNextStatusActions(status: FeedbackStatus): FeedbackStatus[] {
  switch (status) {
    case 'NEW':
      return [
        'ACKNOWLEDGED',
        'PLANNED',
        'DEFERRED',
        'IMPLEMENTING',
        'RESOLVED',
        'CLOSED',
      ]
    case 'ISSUE_CREATED':
      return [
        'ACKNOWLEDGED',
        'PLANNED',
        'DEFERRED',
        'IMPLEMENTING',
        'RESOLVED',
        'CLOSED',
      ]
    case 'ACKNOWLEDGED':
      return ['PLANNED', 'DEFERRED', 'IMPLEMENTING', 'RESOLVED', 'CLOSED']
    case 'PLANNED':
      return ['IMPLEMENTING', 'DEFERRED', 'RESOLVED', 'CLOSED']
    case 'DEFERRED':
      return ['PLANNED', 'IMPLEMENTING', 'RESOLVED', 'CLOSED']
    case 'IMPLEMENTING':
      return ['RESOLVED', 'CLOSED']
    case 'RESOLVED':
      return ['NEW']
    case 'CLOSED':
      return ['NEW']
    case 'FLAGGED_FOR_REVIEW':
      // Issue #7521: the ordinary status buttons are deliberately NOT offered
      // for a held item. Releasing it has to re-run the routing that the hold
      // skipped (create the issue, apply the labels), which a plain status
      // write cannot do — so the disposition lives on the dedicated
      // release/dismiss controls in
      // `src/app/admin/feedback/[id]/feedback-safety-verdict-card.tsx`.
      // Offering "Acknowledge" here would let an admin clear the queue
      // without ever deciding whether the submission was safe.
      return []
    case 'REVIEWED':
      // Legacy status - allow transition to new workflow
      return ['RESOLVED', 'CLOSED', 'NEW']
    default:
      return []
  }
}

/**
 * Returns the CSS classes for status action buttons.
 * Each status has distinct colors for visual clarity.
 */
export function getActionButtonStyle(targetStatus: FeedbackStatus): string {
  switch (targetStatus) {
    case 'NEW':
      return 'bg-status-amber-bg-light text-status-amber-text-strong hover:bg-status-amber-bg border-status-amber-border'
    case 'ISSUE_CREATED':
      return 'bg-status-blue-bg-light text-status-blue-text-strong hover:bg-status-blue-bg border-status-blue-border'
    case 'ACKNOWLEDGED':
      return 'bg-status-sky-bg-light text-status-sky-text-strong hover:bg-status-sky-bg border-status-sky-border'
    case 'PLANNED':
      return 'bg-status-indigo-bg-light text-status-indigo-text-strong hover:bg-status-indigo-bg border-status-indigo-border'
    case 'DEFERRED':
      return 'bg-status-violet-bg-light  text-status-violet-text-strong  hover:bg-status-violet-bg border-status-violet-border '
    case 'IMPLEMENTING':
      return 'bg-status-orange-bg-light text-status-orange-text-strong hover:bg-status-orange-bg border-status-orange-border'
    case 'RESOLVED':
      return 'bg-status-green-bg-light text-status-green-text-strong hover:bg-status-green-bg border-status-green-border'
    case 'CLOSED':
      return 'bg-surface-panel text-text-primary hover:bg-fill-10 border-stroke-light'
    case 'FLAGGED_FOR_REVIEW':
      // UNREACHABLE, and kept for exhaustiveness only (#7521):
      // `getNextStatusActions` returns [] for this status and
      // `isValidFeedbackStatus`'s allowlist excludes it, so nothing renders a
      // button targeting it. Do not read this arm as an invitation to wire a
      // "Hold for Review" control — putting an item into the held state with
      // no verdict behind it gives the review surface nothing to render.
      return 'bg-status-amber-bg-light text-status-amber-text-strong hover:bg-status-amber-bg border-status-amber-border'
    case 'REVIEWED':
      return 'bg-status-purple-bg-light text-status-purple-text-strong hover:bg-status-purple-bg border-status-purple-border'
    default:
      return 'bg-surface-panel text-text-primary hover:bg-fill-10 border-stroke-light'
  }
}

/**
 * Returns the human-readable label for status action buttons.
 */
export function getActionLabel(targetStatus: FeedbackStatus): string {
  switch (targetStatus) {
    case 'NEW':
      return 'Reopen'
    case 'ISSUE_CREATED':
      return 'Issue'
    case 'ACKNOWLEDGED':
      return 'Acknowledge'
    case 'PLANNED':
      return 'Plan'
    case 'DEFERRED':
      return 'Defer'
    case 'IMPLEMENTING':
      return 'Implement'
    case 'RESOLVED':
      return 'Resolve'
    case 'CLOSED':
      return 'Close'
    case 'FLAGGED_FOR_REVIEW':
      // UNREACHABLE — see the same case in `getActionButtonStyle` above.
      return 'Hold for Review'
    case 'REVIEWED':
      return 'Mark Reviewed'
    default:
      return targetStatus
  }
}

/**
 * Returns the keyboard shortcut key for a triage action (Issue #4296).
 */
export function getTriageShortcutKey(
  targetStatus: FeedbackStatus,
): string | null {
  switch (targetStatus) {
    case 'ACKNOWLEDGED':
      return 'A'
    case 'PLANNED':
      return 'P'
    case 'DEFERRED':
      return 'D'
    case 'IMPLEMENTING':
      return 'I'
    case 'RESOLVED':
      return 'V'
    case 'CLOSED':
      return 'X'
    default:
      return null
  }
}

// ============================================================================
// Pin tints (Issue #13578)
// ============================================================================

/**
 * One status's marker colours, as class strings.
 *
 * Three roles rather than one blob: the marker fill and the text sitting ON
 * that fill have to be chosen together to stay readable in both themes, and
 * the popover's status chip is a different contrast problem (text on the page
 * background) that wants the subtle fill instead.
 */
export interface FeedbackPinTint {
  /** Teardrop fill and border. */
  marker: string
  /** Reply count / icon rendered ON the marker fill. */
  label: string
  /** Subtle status chip inside the popover, on the page background. */
  chip: string
}

/**
 * Status -> pin tint. THE source of pin colour; components must not spell a
 * `status-*` class inline (AGENTS.md § Design System, acceptance criterion 3
 * of Issue #13578, asserted by
 * `tests/unit/components/feedback/feedback-pin-tint.test.ts`).
 *
 * ## Why this is not `MEASURABLE_TINT` or `ITEM_COLORS`
 *
 * AGENTS.md names those two as where status tints come from. Neither fits as
 * a container here and extending either would change what it means:
 * `MEASURABLE_TINT` is a single-hue (amber) accent for the Measurable
 * CONCEPT — every key on it is a role of that ONE hue — and `ITEM_COLORS` is
 * keyed by entity kind (`rock`, `group`, `plan`), not by a status enum. A
 * `FeedbackStatus`-keyed map is a third key space, so it lives beside the
 * other feedback status helpers, in the file this feature's issue names.
 * What is borrowed is the PATTERN those two establish: one exported constant,
 * semantic tokens only, roles rather than hues at the call site.
 *
 * ## Hue choices
 *
 * Deliberately the same hue per status as `getActionButtonStyle` above, so a
 * pin and its triage button in the admin detail view read as the same thing.
 * `CLOSED` is the one non-hued entry — a closed thread should recede, and a
 * neutral surface token is how the rest of the app says "inactive".
 */
export const FEEDBACK_PIN_TINT: Record<FeedbackStatus, FeedbackPinTint> = {
  NEW: {
    marker: 'bg-status-amber-bg-strong border-status-amber-border-strong',
    label: 'text-status-amber-text-on-bg',
    chip: 'bg-status-amber-bg-light text-status-amber-text-label',
  },
  ISSUE_CREATED: {
    marker: 'bg-status-blue-bg-strong border-status-blue-border-strong',
    label: 'text-status-blue-text-on-bg',
    chip: 'bg-status-blue-bg-light text-status-blue-text-label',
  },
  ACKNOWLEDGED: {
    marker: 'bg-status-sky-bg-strong border-status-sky-border-strong',
    label: 'text-status-sky-text-on-bg',
    chip: 'bg-status-sky-bg-light text-status-sky-text-label',
  },
  PLANNED: {
    marker: 'bg-status-indigo-bg-strong border-status-indigo-border-strong',
    label: 'text-status-indigo-text-on-bg',
    chip: 'bg-status-indigo-bg-light text-status-indigo-text-label',
  },
  DEFERRED: {
    marker: 'bg-status-violet-bg-strong border-status-violet-border-strong',
    label: 'text-status-violet-text-on-bg',
    chip: 'bg-status-violet-bg-light text-status-violet-text-label',
  },
  IMPLEMENTING: {
    marker: 'bg-status-orange-bg-strong border-status-orange-border-strong',
    label: 'text-status-orange-text-on-bg',
    chip: 'bg-status-orange-bg-light text-status-orange-text-label',
  },
  RESOLVED: {
    marker: 'bg-status-green-bg-strong border-status-green-border-strong',
    label: 'text-status-green-text-on-bg',
    chip: 'bg-status-green-bg-light text-status-green-text-label',
  },
  CLOSED: {
    marker: 'bg-fill-10 border-stroke-regular',
    label: 'text-text-primary',
    chip: 'bg-surface-panel text-text-secondary',
  },
  REVIEWED: {
    marker: 'bg-status-purple-bg-strong border-status-purple-border-strong',
    label: 'text-status-purple-text-on-bg',
    chip: 'bg-status-purple-bg-light text-status-purple-text-label',
  },
  FLAGGED_FOR_REVIEW: {
    marker: 'bg-status-red-bg-strong border-status-red-border-strong',
    label: 'text-status-red-text-on-bg',
    chip: 'bg-status-red-bg-light text-status-red-text-label',
  },
}

/**
 * The tint for a status, falling back to `NEW` for a value the map does not
 * know.
 *
 * The fallback is not theoretical: `FeedbackStatus` is a Prisma enum, and a
 * migration can add a value in a PR that does not touch this file. An
 * unmapped status must still render a VISIBLE marker — an undefined tint
 * would produce a transparent, borderless pin, which reads as a missing pin
 * rather than as a styling bug.
 */
export function getPinTint(status: FeedbackStatus): FeedbackPinTint {
  return FEEDBACK_PIN_TINT[status] ?? FEEDBACK_PIN_TINT.NEW
}
